import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  DischargeState,
  DischargeResponse,
  DischargeField,
  DischargeSummary,
  DischargeSection,
  WizardStep,
  IDischargeDoctorType,
} from "../interfaces/IDischarge";
import { fetchSchemesOnBoardingById } from "../apis/schemeService";
import { ISchemeOnBoardById } from "../interfaces/IScheme";
import { dischargeService, dischargeServiceIhx, dischargeServiceNew, dischargeSummary } from "../apis/patientService";
import { uploadDocs } from "../apis/caseService";
import { act } from "react";
import { dischargeServiceEchs } from "../apis/SchemeService/ECHS/echsService";
import { dischargeServiceCghs, dischargeServiceCghsNew } from "../apis/SchemeService/CGHS/cghsService";
import { dischargeServiceStarhealth } from "../apis/SchemeService/STARHEALTH/starHealthService";

const initialState: DischargeState = {
  allWizardSteps: [
    // { step: 0, title: "Basic Details", canNavigate: true, completed: false },
    // { step: 1, title: "Billing Details", canNavigate: true, completed: false },
  ],
  activeStep: 0,
  status: null,
  dischargeData: null,
  isLoading: false,
  selectedFile: null,
  error: null,
  editMode: false,
  editedFields: {},
  selectedScheme: null,
  isFetchingSchemeById: false,
  isFetchingTypeList: false,
  doctorTypeList: null,
  starHealthRoomTypeList: null,
  isSubmitting: false,
  isSubmittingClaimId: false,
  dischargeSummary: {
    procedureList: [],
    procedureFields: {},
    selectedProcedure: null,
    selectedProcedureFields: [],
    isFetchingProcedureFieldList: false,
    isDownloadingPDF: false,
    patientDetails: null,
    dischargeMedicine: null,
    dischargeSymptoms: null,
  },
  dischargeID: null,
  dischargeSummaryResponse: null,
};

export const fetchSchemeById = createAsyncThunk(
  "discharge/fetchSchemeById",
  async (schemeMetaDataId: number) => {
    const response = await fetchSchemesOnBoardingById(schemeMetaDataId);
    return response;
  }
);

export const fetchDoctorTypeList = createAsyncThunk(
  "discharge/fetchDoctorTypeList",
  async (data: any) => {
    const response = await dischargeService.getDoctorTypeList(data);
    return response;
  }
);

export const fetchDoctorTypeListNew = createAsyncThunk(
  "discharge/fetchDoctorTypeList",
  async (data: any) => {
    const response = await dischargeServiceNew.getDoctorTypeList(data);
    return response;
  }
);

export const dischargeSummaryReaderResponse = createAsyncThunk(
  "discharge/dischargeSummaryReaderResponse",
  async (data: any) => {
    const response = await dischargeService.dischargeSummaryReader(data);
    return response;
  }
);

export const fetchStarHealthRoomTypeList = createAsyncThunk(
  "discharge/fetchStarHealthRoomTypeList",
  async (portalMetaDataId: number) => {
    const response = await dischargeService.getStarHealthRoomTypeList(
      portalMetaDataId
    );
    return response;
  }
);

export const submitDischargeDataEchs = createAsyncThunk(
  "discharge/submitDischargeDataEchs",
  async ({
    caseId,
    visitId,
    portalMetaDataId,
    caseDischargeId,
    dischargeData,
  }: {
    caseId: any;
    visitId: any;
    portalMetaDataId: number;
    caseDischargeId: number | null;
    dischargeData: any;
  }, { rejectWithValue }) => {
    try {
      const response = await dischargeServiceEchs.saveDischargeData(
        caseId,
        visitId,
        portalMetaDataId,
        caseDischargeId,
        dischargeData
      );
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const submitDischargeDataInEdgeProEchs = createAsyncThunk(
  "discharge/submitDischargeDataInEdgeProEchs",
  async (data: any, { rejectWithValue }) => {
    try {
      const response = await dischargeServiceEchs.saveDischargeDataInEdgePro(
        data.caseId,
        data.visitId,
        data.portalMetaDataId,
        data.caseDischargeId,
        data.dischargeData
      );
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const intimateSubmitDischargeEchs = createAsyncThunk(
  "discharge/intimateSubmitDischargeEchs",
  async (data: any, { rejectWithValue }) => {
    try {
      const response = await dischargeServiceEchs.saveIntimateDischarge(
        data.caseId,
        data.visitId,
        data.portalMetaDataId,
        data.intimationData
      );
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const submitDischargeData = createAsyncThunk(
  "discharge/submitDischargeData",
  async (data: any, { rejectWithValue }) => {
    try {
      const response = await dischargeService.saveDischargeData(
        data.caseId,
        data.visitId,
        data.portalMetaDataId,
        data.caseDischargeId,
        data.dischargeData
      );
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const submitDischargeDataIhx = createAsyncThunk(
  "discharge/submitDischargeDataIhx",
  async (data: any, { rejectWithValue }) => {
    try {
      const response = await dischargeServiceIhx.saveDischargeData(
        data.caseId,
        data.visitId,
        data.portalMetaDataId,
        data.caseDischargeId,
        data.dischargeData
      );
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const submitDischargeDataCghs = createAsyncThunk(
  "discharge/submitDischargeDataCghs",
  async (data: any, { rejectWithValue }) => {
    try {
      const response = await dischargeServiceCghs.saveDischargeData(
        data.caseId,
        data.visitId,
        data.portalMetaDataId,
        data.caseDischargeId,
        data.dischargeData
      );
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const submitDischargeDataCghsNew = createAsyncThunk(
  "discharge/submitDischargeDataCghsNew",
  async (data: any, { rejectWithValue }) => {
    try {
      const response = await dischargeServiceCghsNew.saveDischargeData(
        data.caseId,
        data.visitId,
        data.portalMetaDataId,
        data.caseDischargeId,
        data.dischargeData
      );
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const submitDischargeDataInEdgePro = createAsyncThunk(
  "discharge/submitDischargeDataInEdgePro",
  async (data: any, { rejectWithValue }) => {
    try {
      const response = await dischargeService.saveDischargeDataInEdgePro(
        data.caseId,
        data.visitId,
        data.portalMetaDataId,
        data.caseDischargeId,
        data.dischargeData
      );
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);
export const submitDischargeDataInEdgeProIhx = createAsyncThunk(
  "discharge/submitDischargeDataInEdgeProIhx",
  async (data: any, { rejectWithValue }) => {
    try {
      const response = await dischargeServiceIhx.saveDischargeDataInEdgePro(
        data.caseId,
        data.visitId,
        data.portalMetaDataId,
        data.caseDischargeId,
        data.dischargeData
      );
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const submitDischargeDataInEdgeProCghs = createAsyncThunk(
  "discharge/submitDischargeDataInEdgeProCghs",
  async (data: any, { rejectWithValue }) => {
    try {
      const response = await dischargeServiceCghs.saveDischargeDataInEdgePro(
        data.caseId,
        data.visitId,
        data.portalMetaDataId,
        data.caseDischargeId,
        data.dischargeData
      );
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const submitDischargeDataInEdgeProCghsNew = createAsyncThunk(
  "discharge/submitDischargeDataInEdgeProCghsNew",
  async (data: any, { rejectWithValue }) => {
    try {
      const response = await dischargeServiceCghsNew.saveDischargeDataInEdgePro(
        data.caseId,
        data.visitId,
        data.portalMetaDataId,
        data.caseDischargeId,
        data.dischargeData
      );
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const intimateSubmitDischarge = createAsyncThunk(
  "discharge/intimateSubmitDischarge",
  async (data: any, { rejectWithValue }) => {
    try {
      const response = await dischargeService.saveIntimateDischarge(
        data.caseId,
        data.visitId,
        data.portalMetaDataId,
        data.intimationData
      );
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const intimateSubmitDischargeIhx = createAsyncThunk(
  "discharge/intimateSubmitDischargeIhx",
  async (data: any, { rejectWithValue }) => {
    try {
      const response = await dischargeServiceIhx.saveIntimateDischarge(
        data.caseId,
        data.visitId,
        data.portalMetaDataId,
        data.intimationData
      );
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const intimateSubmitDischargeCghs = createAsyncThunk(
  "discharge/intimateSubmitDischarge",
  async (data: any, { rejectWithValue }) => {
    try {
      const response = await dischargeServiceCghs.saveIntimateDischarge(
        data.caseId,
        data.visitId,
        data.portalMetaDataId,
        data.intimationData
      );
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const intimateSubmitDischargeCghsNew = createAsyncThunk(
  "discharge/intimateSubmitDischargeCghsNew",
  async (data: any, { rejectWithValue }) => {
    try {
      const response = await dischargeServiceCghsNew.saveIntimateDischarge(
        data.caseId,
        data.visitId,
        data.portalMetaDataId,
        data.intimationData
      );
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);


export const patchClaimId = createAsyncThunk(
  "discharge/patchClaimId",
  async (data: any, { rejectWithValue }) => {
    try {
      const response = await dischargeService.updateClaimId(
        data.formData,
        data.caseId,
        data.visitId,
        data.schemeId
      );
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const UploadDocument = createAsyncThunk(
  "enhancement/UploadDocument",
  async ({ formData, accessToken }: { formData: any; accessToken: string }) => {
    const response = await uploadDocs(formData, accessToken);
    return response;
  }
);

export const fetchProcedureField = createAsyncThunk(
  "dischargeSummary/fetchProcedureField",
  async (data: any) => {
    const response = await dischargeSummary.getProcedureList(data);

    return response;
  }
);

export const downloadDischargeSummaryPDF = createAsyncThunk(
  "dischargeSummary/downloadDischargeSummaryPDF",
  async (data: any) => {
    const response = await dischargeSummary.downloadDischargeSummaryPDF(data);

    // return response;
  }
);

export const submitDischargeDataStarHealth = createAsyncThunk(
  "discharge/submitDischargeDataStarHealth",
  async (data: any, { rejectWithValue }) => {
    try {
      const response = await dischargeServiceStarhealth.saveDischargeData(
        data.caseId,
        data.visitId,
        data.portalMetaDataId,
        data.caseDischargeId,
        data.dischargeData
      );
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const submitDischargeDataInEdgeProStarHealth = createAsyncThunk(
  "discharge/submitDischargeDataInEdgeProStarHealth",
  async (data: any, { rejectWithValue }) => {
    try {
      const response = await dischargeServiceStarhealth.saveDischargeDataInEdgePro(
        data.caseId,
        data.visitId,
        data.portalMetaDataId,
        data.caseDischargeId,
        data.dischargeData
      );
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const intimateSubmitDischargeStarHealth = createAsyncThunk(
  "discharge/intimateSubmitDischargeStarHealth",
  async (data: any, { rejectWithValue }) => {
    try {
      const response = await dischargeServiceStarhealth.saveIntimateDischarge(
        data.caseId,
        data.visitId,
        data.portalMetaDataId,
        data.intimationData
      );
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

const dischargeSlice = createSlice({
  name: "discharge",
  initialState,
  reducers: {
    setWizardSteps: (state, action: PayloadAction<Array<WizardStep>>) => {
      state.allWizardSteps = action.payload;
    },
    updateWizardStep(state, action: PayloadAction<number>) {
      const newStep = action.payload;
      if (
        typeof newStep === "number" &&
        newStep >= 0 &&
        newStep < state.allWizardSteps.length
      ) {
        state.activeStep = newStep;
        state.allWizardSteps?.forEach((step, index) => {
          step.canNavigate =
            index <= newStep && (index === newStep || step.completed);
        });
      }
    },
    completeWizardStep(state, action: PayloadAction<number>) {
      const completedStep = action.payload;
      if (
        typeof completedStep === "number" &&
        completedStep >= 0 &&
        completedStep < state.allWizardSteps.length
      ) {
        state.allWizardSteps[completedStep].completed = true;
        state.allWizardSteps?.forEach((step, index) => {
          step.canNavigate =
            index <= completedStep &&
            (index === completedStep || step.completed);
        });
      }
    },
    setDischargeDataStart(state) {
      state.isLoading = true;
      state.error = null;
    },
    setDischargeDataSuccess(state, action: PayloadAction<DischargeResponse>) {
      state.dischargeData = action.payload;
      state.isLoading = false;
    },
    setDischargeDataFailure(state, action: PayloadAction<string>) {
      state.isLoading = false;
      state.error = action.payload;
    },
    setSelectedFile: (
      state,
      action: PayloadAction<{ file: File; documentType: string } | null>
    ) => {
      state.selectedFile = action.payload;
    },
    toggleEditMode(state) {
      state.editMode = !state.editMode;
      if (!state.editMode) {
        state.editedFields = {};
      }
    },
    setToggleEditMode(state, action: PayloadAction<boolean>) {
      state.editMode = action.payload;
      // if (!state.editMode) {
      //   state.editedFields = {};
      // }
    },
    setCaseDischargeValue(state, action: PayloadAction<any>) {
      state.dischargeID = action.payload;
    },
    updateField(
      state,
      action: PayloadAction<{
        sectionKey: string;
        fieldId: string | undefined;
        value: any;
      }>
    ) {
      const { sectionKey, fieldId, value } = action.payload;
      if (fieldId) {
        if (sectionKey === "all" && fieldId === "all") {
          state.editedFields = value;
        } else {
          if (!state.editedFields[sectionKey]) {
            state.editedFields[sectionKey] = {};
          }
          state.editedFields[sectionKey][fieldId] = value;
        }
      }
    },
    updateDischargeDataField(
      state: DischargeState,
      action: PayloadAction<{
        sectionKey: keyof DischargeResponse; // Type-safe section keys
        fieldId: string | undefined;
        updateValues: any;
      }>
    ) {
      const { sectionKey, fieldId, updateValues } = action.payload;

      // Ensure dischargeData is not null
      if (!state.dischargeData) {
        console.error("Discharge data is not available.");
        return;
      }

      // Update the specific field in the correct section
      const currentSection = state.allWizardSteps.find(
        (item) => item.step === state.activeStep
      );
      const Allfields = currentSection
        ? state.dischargeData[sectionKey]?.[0]?.[currentSection.title]
        : null;

      if (Allfields) {
        const fieldToUpdate = Allfields.find((item) => item.id === fieldId);

        if (fieldToUpdate && fieldToUpdate?.data) {
          fieldToUpdate.data = updateValues.data;
          fieldToUpdate.documentName = updateValues.documentName;
          fieldToUpdate.type =
            fieldToUpdate.type == "document-show"
              ? "document-upload"
              : fieldToUpdate.type;
        }
      }
    },
    saveChanges(state) {
      if (state.dischargeData && state.editedFields) {
        const newDischargeData = JSON.parse(
          JSON.stringify(state.dischargeData)
        );
        Object.entries(state.editedFields)?.forEach(([sectionKey, fields]) => {
          const section =
            newDischargeData[sectionKey as keyof DischargeResponse];
          if (Array.isArray(section)) {
            section?.forEach((sectionData) => {
              Object.entries(fields)?.forEach(([fieldId, value]) => {
                const fieldIndex = sectionData[
                  Object.keys(sectionData)[0]
                ].findIndex((f: DischargeField) => f.id === fieldId);
                if (fieldIndex !== -1) {
                  sectionData[Object.keys(sectionData)[0]][fieldIndex] = {
                    ...sectionData[Object.keys(sectionData)[0]][fieldIndex],
                    data: value,
                  };
                }
              });
            });
          }
        });
        state.dischargeData = newDischargeData;
      }
      state.editMode = false;
      state.editedFields = {};
    },
    setDischargeSummary(state, action) {
      if (action.payload.procedureList) {
        state.dischargeSummary.procedureList = action.payload.procedureList;
      }
    },
    updateDischargeSummary(state, action) {
      const { key, selectedProcedure, procedureFields } = action.payload;

      if (key && selectedProcedure && procedureFields) {
        state.dischargeSummary.selectedProcedure = selectedProcedure;
        state.dischargeSummary.procedureFields = {
          [selectedProcedure.name]: procedureFields,
        };
      }
    },
    updatePatientDetails(state, action) {
      state.dischargeSummary.patientDetails = action.payload;
    },
    updateDischargeMedicine(state, action) {
      state.dischargeSummary.dischargeMedicine = action.payload;
    },
    updateDischargeSymptoms(state, action) {
      state.dischargeSummary.dischargeSymptoms = action.payload;
    },
    updateEditedFields: (state, action: PayloadAction<any>) => {
      state.editedFields = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSchemeById.pending, (state) => {
        state.isFetchingSchemeById = true;
      })
      .addCase(
        fetchSchemeById.fulfilled,
        (state, action: PayloadAction<ISchemeOnBoardById>) => {
          state.isFetchingSchemeById = false;
          if (action.payload?.schemeRes) {
            state.selectedScheme = action?.payload?.schemeRes;
          }
        }
      )
      .addCase(fetchSchemeById.rejected, (state) => {
        state.isFetchingSchemeById = false;
        state.error = "Unable to Fetch scheme on board by id";
      })
      .addCase(fetchDoctorTypeList.pending, (state) => {
        state.isFetchingTypeList = true;
      })
      .addCase(
        fetchDoctorTypeList.fulfilled,
        (state, action: PayloadAction<Array<IDischargeDoctorType>>) => {
          state.isFetchingTypeList = false;
          if (action.payload) {
            state.doctorTypeList = action.payload;
          }
        }
      )
      .addCase(fetchDoctorTypeList.rejected, (state) => {
        state.isFetchingTypeList = false;
        state.error = "Unable to Fetch doctor list";
      })
      .addCase(fetchStarHealthRoomTypeList.pending, (state) => {
        state.isFetchingTypeList = true;
      })
      .addCase(
        fetchStarHealthRoomTypeList.fulfilled,
        (
          state,
          action: PayloadAction<Array<{ id: number; roomType: string }>>
        ) => {
          state.isFetchingTypeList = false;
          if (action.payload) {
            state.starHealthRoomTypeList = action.payload;
          }
        }
      )
      .addCase(fetchStarHealthRoomTypeList.rejected, (state) => {
        state.isFetchingTypeList = false;
        state.error = "Unable to Fetch Room Type list";
      })
      .addCase(submitDischargeData.pending, (state) => {
        state.isSubmitting = true;
      })
      .addCase(submitDischargeData.fulfilled, (state) => {
        state.isSubmitting = false;
        state.status = true;
        // Handle successful submission if needed
      })
      .addCase(submitDischargeData.rejected, (state) => {
        state.isSubmitting = false;
        state.status = false;
        // Handle submission error if needed
      })
      .addCase(intimateSubmitDischarge.pending, (state) => {
        state.isSubmitting = true;
      })
      .addCase(intimateSubmitDischarge.fulfilled, (state) => {
        state.isSubmitting = false;

        // Handle successful submission if needed
      })
      .addCase(intimateSubmitDischarge.rejected, (state, action) => {
        state.isSubmitting = false;

        // Handle submission error if needed
      })
      .addCase(patchClaimId.pending, (state) => {
        state.isSubmittingClaimId = true;
      })
      .addCase(patchClaimId.fulfilled, (state) => {
        state.isSubmittingClaimId = false;

        // Handle successful submission if needed
      })
      .addCase(patchClaimId.rejected, (state, action) => {
        state.isSubmittingClaimId = false;

        // Handle submission error if needed
      })

      .addCase(UploadDocument.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(
        UploadDocument.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.isLoading = false;
        }
      )
      .addCase(UploadDocument.rejected, (state) => {
        state.isLoading = false;
      })
      .addCase(fetchProcedureField.pending, (state) => {
        state.dischargeSummary.isFetchingProcedureFieldList = true;
      })
      .addCase(fetchProcedureField.fulfilled, (state, action) => {
        state.dischargeSummary.isFetchingProcedureFieldList = false;
        if (action.payload && state.dischargeSummary.selectedProcedure) {
          const procedureFields = action.payload;
          delete procedureFields.id;
          delete procedureFields.procedureName;
          state.dischargeSummary.procedureFields[
            state.dischargeSummary.selectedProcedure.name
          ] = procedureFields;
        }
      })
      .addCase(fetchProcedureField.rejected, (state) => {
        state.dischargeSummary.isFetchingProcedureFieldList = false;
      })
      .addCase(downloadDischargeSummaryPDF.pending, (state) => {
        state.dischargeSummary.isDownloadingPDF = true;
      })
      .addCase(downloadDischargeSummaryPDF.fulfilled, (state) => {
        state.dischargeSummary.isDownloadingPDF = false;
      })
      .addCase(downloadDischargeSummaryPDF.rejected, (state, action) => {
        state.dischargeSummary.isDownloadingPDF = false;
      })
      .addCase(dischargeSummaryReaderResponse.pending, (state) => {
        state.isLoading = true;
        state.dischargeSummaryResponse = null;
      })
      .addCase(
        dischargeSummaryReaderResponse.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.isLoading = false;
          state.dischargeSummaryResponse = action.payload;
        }
      )
      .addCase(dischargeSummaryReaderResponse.rejected, (state) => {
        state.isLoading = false;
        state.dischargeSummaryResponse = null;
      })
      .addCase(submitDischargeDataStarHealth.pending, (state) => {
        state.isSubmitting = true;
      })
      .addCase(submitDischargeDataStarHealth.fulfilled, (state) => {
        state.isSubmitting = false;
        state.status = true;
        // Handle successful submission if needed
      })
      .addCase(submitDischargeDataStarHealth.rejected, (state) => {
        state.isSubmitting = false;
        state.status = false;
        // Handle submission error if needed
      })
      .addCase(intimateSubmitDischargeStarHealth.pending, (state) => {
        state.isSubmitting = true;
      })
      .addCase(intimateSubmitDischargeStarHealth.fulfilled, (state) => {
        state.isSubmitting = false;

        // Handle successful submission if needed
      })
      .addCase(intimateSubmitDischargeStarHealth.rejected, (state, action) => {
        state.isSubmitting = false;

        // Handle submission error if needed
      });
  },
});

export const {
  setWizardSteps,
  updateWizardStep,
  completeWizardStep,
  setDischargeDataStart,
  setDischargeDataSuccess,
  setDischargeDataFailure,
  setSelectedFile,
  toggleEditMode,
  updateField,
  updateDischargeDataField,
  saveChanges,
  setToggleEditMode,
  setDischargeSummary,
  updateDischargeSummary,
  updatePatientDetails,
  updateDischargeMedicine,
  updateDischargeSymptoms,
  updateEditedFields,
  setCaseDischargeValue,
} = dischargeSlice.actions;

export default dischargeSlice.reducer;
